/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
import { useContext, useEffect, useReducer } from 'react';
import isEmpty from 'lodash/isEmpty';
import { getTranslation } from '../../../../../ui-kitchen/src/providers/TranslationProvider';
import { getLocationPath } from '../../../../../ui-kitchen/src/utils/locationUtils';
import {
  findContent, hasActiveSafetyCategories, getCustomAffiliationId, getCustomAffiliationIdList, initFbPixel,
} from './venueDetailsUtils';
import IframeHostContext from '../../contexts/iframeHost';
import { useRouteParams } from '../../utils/legacyUtils';

// useVenueState Custom Hook
//
// Maintains the more complicated Venue data state with a useReducer setup.
// Manages pre- and post-load effects for Venue data load.
// Returns [venueState, dispatchVenueState]

const reducer = (state, action) => {
  switch (action.type) {
    case 'setAllEvents': {
      return {
        ...state, allEvents: action.payload,
      };
    }
    case 'setScrollTop': {
      return {
        ...state, scrollTop: +action.payload,
      };
    }
    case 'initializeVenueData': {
      const venue = action.payload;
      const {
        // eslint-disable-next-line camelcase
        customAffiliation, customAffiliations, fb_pixel, featureRecaptcha, id: venueId, largePartyMessage, name: venueName, safetyCategories, venueGroup,
      } = venue;
      const nextState = {
        aboutVenue: findContent(venue, 'about'),
        customAffiliation: getCustomAffiliationId(customAffiliation),
        customAffiliations: getCustomAffiliationIdList(customAffiliation, customAffiliations, venueGroup),
        fb_pixel,
        featureRecaptcha,
        hasActiveSafetyCategories: hasActiveSafetyCategories(safetyCategories),
        largePartyMessage,
        needToKnow: findContent(venue, 'need_to_know'),
        safetyCategories,
        tagline: findContent(venue, 'tagline'),
        venue,
        venueGroup,
        venueId,
        venueName,
      };
      return {
        ...state, ...nextState,
      };
    }
    case 'setParntershipBranding': {
      return {
        ...state, partnershipBranding: action.payload,
      };
    }
    default: return state;
  }
};

const addOnsRegex = /\/venues\/[0-9]+\/add-ons/;
const initialState = ({
  customAffiliations: [],
  isAddOns: addOnsRegex.test(getLocationPath()),
  venue: { },
});

const useVenueState = ({
  Analytics,
  isAddOns,
  reset,
  setCountry,
  setMetadataPageTitle,
  setMobileFirstLogin,
  setNavVenueId,
  setNavVenueName,
  setSelectedCoreSeatsMinMax,
  setUserLocationLatLong,
  signal,
}) => {
  const iframeHost = useContext(IframeHostContext);
  const { venueId } = useRouteParams();
  const initializer = (initState) => ({
    ...initState,
    scrollTop: +iframeHost.getScrollTop(),
    venueId: venueId ? +venueId : undefined,
  });
  const [venueState, dispatchVenueState] = useReducer(reducer, initialState, initializer);

  // Listen for ScrollTop signal
  useEffect(() => {
    if (signal.name === 'SCROLL_TOP') {
      dispatchVenueState({ type: 'setScrollTop', payload: signal.args });
    }
  }, [signal]);

  // Initial pre-load side-effects
  useEffect(() => {
    // reset booking info if we are looking at booking bricks as
    // this controller services 2 pages - venue details and menus/add-ons
    if (!isAddOns) {
      reset();
    }
    setNavVenueId(venueState.venueId);
  }, []);

  // venue post-load side-effects on venueState.venue change
  useEffect(() => {
    if (!isEmpty(venueState.venue)) {
      const {
        // eslint-disable-next-line camelcase
        fb_pixel, isMobileFirstLogin, location, largePartyMessage, maxPartySize, minPartySize, name,
      } = venueState.venue;

      const venueDetailTitle = getTranslation({
        translationKey: 'titles.venue_details',
        translationValues: { venueName: name },
        fallback: `Book Your ${name} Reservation Now on Resy`,
      });

      // dispatches
      setMetadataPageTitle(venueDetailTitle);
      setNavVenueName(name);
      setMobileFirstLogin(isMobileFirstLogin);
      setCountry(location?.country);
      setUserLocationLatLong(location?.coords);
      setSelectedCoreSeatsMinMax({
        isLargePartyConfigured: !!largePartyMessage,
        max: maxPartySize,
        min: minPartySize,
      });

      // analytics
      initFbPixel(fb_pixel);
      Analytics.venuePixel(fb_pixel);
      Analytics?.setVenueName?.(name);
      Analytics?.setVenueId?.(venueState.venue.id);
    }
  }, [venueState.venue]);

  useEffect(() => () => {
    Analytics?.setVenueName?.(null);
    Analytics?.setVenueId?.(null);
  }, []);

  return [venueState, dispatchVenueState];
};

export default useVenueState;
